const GALLERY_SELECTOR = '[data-dg-card-gallery]';
const SWIPE_THRESHOLD = 24;

class DgCardGallery {
  constructor(root) {
    this.root = root;
    this.images = Array.from(root.querySelectorAll('.dg-card-gallery__image'));
    this.dots = Array.from(root.querySelectorAll('.dg-card-gallery__dot'));
    this.currentIndex = 0;
    this.touchStartX = null;
    this.touchStartY = null;
    this.didSwipe = false;
    this.lockedAxis = null;
  }

  init() {
    if (this.images.length < 2) return;

    this.root.dataset.dgCardGalleryBound = 'true';
    this.update();

    this.root.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.root.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.root.addEventListener('touchend', this.onTouchEnd, { passive: false });
    this.root.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
    this.root.addEventListener('click', this.onClick);
  }

  onTouchStart = (event) => {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.lockedAxis = null;
  };

  onTouchMove = (event) => {
    if (this.touchStartX == null || this.touchStartY == null || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (this.lockedAxis == null) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      this.lockedAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
    }

    if (this.lockedAxis === 'x') {
      event.preventDefault();
    }
  };

  onTouchEnd = (event) => {
    if (this.touchStartX == null || this.touchStartY == null) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) >= SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.didSwipe = true;

      if (deltaX < 0) {
        this.next();
      } else {
        this.previous();
      }

      window.setTimeout(() => {
        this.didSwipe = false;
      }, 250);
    }

    this.resetTouchState();
  };

  onTouchCancel = () => {
    this.resetTouchState();
  };

  onClick = (event) => {
    if (this.didSwipe) {
      event.preventDefault();
      event.stopPropagation();
      this.didSwipe = false;
      return;
    }

    const link = this.root.dataset.dgCardGalleryLink;
    if (!link) return;

    if (event.target instanceof Element && event.target.closest('a, button')) return;

    window.location.href = link;
  };

  resetTouchState() {
    this.touchStartX = null;
    this.touchStartY = null;
    this.lockedAxis = null;
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.update();
  }

  previous() {
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.update();
  }

  update() {
    this.images.forEach((image, index) => {
      image.classList.toggle('is-active', index === this.currentIndex);
    });

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentIndex);
    });
  }
}

const initCardGalleries = (root = document) => {
  root.querySelectorAll(`${GALLERY_SELECTOR}:not([data-dg-card-gallery-bound])`).forEach((gallery) => {
    new DgCardGallery(gallery).init();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCardGalleries(), { once: true });
} else {
  initCardGalleries();
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;

      if (node.matches?.(GALLERY_SELECTOR)) {
        initCardGalleries(node.parentElement || node);
        return;
      }

      if (node.querySelector?.(GALLERY_SELECTOR)) {
        initCardGalleries(node);
      }
    });
  }
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
